import { Link } from "react-router-dom";
import type { MergedAccountView } from "@sales-ops-amplify/shared";
import { CompanyLogo } from "@/components/companies/company-logo";
import { cn } from "@/cn";

export const COMPANY_TABS = [
  { id: "overview", label: "Overview" },
  { id: "activities", label: "Activities" },
  { id: "people", label: "People" },
  { id: "opportunities", label: "Opportunities" },
  { id: "sequences", label: "Sequences" },
  { id: "conversations", label: "Conversations" },
  { id: "enrichment", label: "Enrichment" },
  { id: "fields", label: "All Fields" },
] as const;

export type CompanyTabId = (typeof COMPANY_TABS)[number]["id"];

export function isCompanyTabId(value: string | undefined): value is CompanyTabId {
  return COMPANY_TABS.some((t) => t.id === value);
}

export function CompanyHero({
  merged,
  isMutable,
  companiesBase,
  industry,
  locationLabel,
  website,
  linkedinUrl,
  onSave,
  onEnrich,
  onResearch,
  onDelete,
  onFindPeople,
  saving,
  enriching,
  researching,
  deleting,
}: {
  merged: MergedAccountView;
  isMutable: boolean;
  companiesBase: "/companies" | "/user/accounts";
  industry?: string;
  locationLabel?: string;
  website?: string;
  linkedinUrl?: string;
  onSave?: () => void;
  onEnrich?: () => void;
  onResearch?: () => void;
  onDelete?: () => void;
  onFindPeople?: () => void;
  saving?: boolean;
  enriching?: boolean;
  researching?: boolean;
  deleting?: boolean;
}) {
  const name = merged.name;
  const user = merged.user;
  const directory = merged.directory;
  const domain = user?.domain ?? directory?.domain;

  return (
    <div className="min-w-0 max-w-full space-y-4 overflow-x-hidden border-b border-border pb-4">
      <nav className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
        <Link
          to={companiesBase}
          className="shrink-0 hover:text-brand hover:underline"
        >
          {isMutable ? "Accounts" : "Find companies"}
        </Link>
        <span aria-hidden className="shrink-0">
          /
        </span>
        <span className="min-w-0 truncate text-foreground">{name}</span>
      </nav>

      <div className="flex min-w-0 flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 max-w-full items-start gap-3">
          <CompanyLogo
            name={name}
            logoUrl={directory?.logoUrl ?? user?.logoUrl}
            domain={domain}
            size="lg"
          />
          <div className="min-w-0 max-w-full">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h1 className="break-words text-2xl font-semibold tracking-tight text-foreground">
                {name}
              </h1>
              {linkedinUrl ? (
                <a
                  href={linkedinUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 text-xs text-brand hover:underline"
                >
                  LinkedIn
                </a>
              ) : null}
              {website ? (
                <a
                  href={website.startsWith("http") ? website : `https://${website}`}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 text-xs text-brand hover:underline"
                >
                  Website
                </a>
              ) : null}
            </div>
            <p className="mt-1 break-words text-sm text-muted-foreground">
              {[industry, locationLabel].filter(Boolean).join(" · ") || "—"}
            </p>
            {user?.status ? (
              <span className="mt-2 inline-flex rounded-md border border-border bg-muted/40 px-2 py-0.5 text-[11px] font-medium capitalize text-muted-foreground">
                {user.status}
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {onFindPeople ? (
            <button
              type="button"
              onClick={onFindPeople}
              className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted/40"
            >
              Find people
            </button>
          ) : null}
          {isMutable && onResearch ? (
            <button
              type="button"
              disabled={researching}
              onClick={onResearch}
              className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted/40 disabled:opacity-50"
            >
              {researching ? "Researching…" : "Research"}
            </button>
          ) : null}
          {onEnrich ? (
            <button
              type="button"
              disabled={enriching}
              onClick={onEnrich}
              className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted/40 disabled:opacity-50"
            >
              {enriching ? "Enriching…" : "Enrich"}
            </button>
          ) : null}
          {!isMutable && !user && onSave ? (
            <button
              type="button"
              disabled={saving}
              onClick={onSave}
              className={cn(
                "rounded-md bg-brand px-2.5 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-50",
              )}
            >
              {saving ? "Saving…" : "Save to accounts"}
            </button>
          ) : null}
          {!isMutable && user ? (
            <Link
              to={`/user/accounts/${user.accountId}`}
              className="rounded-md bg-brand px-2.5 py-1.5 text-xs font-medium text-brand-foreground"
            >
              Open my copy
            </Link>
          ) : null}
          {isMutable && onDelete ? (
            <button
              type="button"
              disabled={deleting}
              onClick={onDelete}
              className="rounded-md border border-destructive/40 px-2.5 py-1.5 text-xs text-destructive disabled:opacity-50"
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
